import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { UUID_PATTERN } from '../../shared/uuid';
/**
 * Export ids are `randomUUID()` values. The pattern is enforced on both sides
 * because the id becomes a path segment of the storage key — anything else
 * must never reach the object store.
 */ export const OMNISCIENT_EXPORT_ID_REGEX = new RegExp(`^${UUID_PATTERN}$`, 'i');
/**
 * Route template of the download endpoint. Owned here rather than in the
 * contract so the path a generated export advertises and the path the endpoint
 * answers on cannot drift apart.
 */ export const OMNISCIENT_EXPORT_DOWNLOAD_PATH_TEMPLATE = '/api/v2/omniscient/exports/:exportId/download';
/**
 * Exactly the paths the generating tool minted. Nothing writes one any more —
 * exports moved to the app file store — but a MIGRATED transcript still carries
 * them, and the client feeds a persisted `downloadUrl` straight into its API
 * client, so the value stays pinned to this one route: a rewritten transcript
 * can never turn the download button into a request to some other endpoint.
 */ export const OMNISCIENT_EXPORT_DOWNLOAD_PATH_REGEX = new RegExp(`^${OMNISCIENT_EXPORT_DOWNLOAD_PATH_TEMPLATE.replace(':exportId', UUID_PATTERN)}$`, 'i');
export const OMNISCIENT_EXPORT_XLSX_MEDIA_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/**
 * Metadata of a generated Omniscient export, as the generating tool reports it
 * into the chat turn (the download card renders from this).
 *
 * `expiresAt` is the RETENTION deadline: exports are ephemeral, the object
 * store drops the `exports/` prefix after ~24h. It is deliberately NOT the
 * lifetime of a download URL — see `DownloadOmniscientExportResponseSchema`.
 */ export const OmniscientExportMetadataSchema = z.object({
    exportId: z.string().regex(OMNISCIENT_EXPORT_ID_REGEX),
    filename: z.string().min(1),
    // Excel-only in v1 is a product decision, so the contract enforces it rather
    // than trusting a tool to report an honest media type. Widen to an enum when
    // a second format lands.
    mediaType: z.literal(OMNISCIENT_EXPORT_XLSX_MEDIA_TYPE),
    expiresAt: z.string()
});
/**
 * Response of `GET /omniscient/exports/:exportId/download`.
 *
 * DECISION: JSON carrying a short-lived presigned URL, NOT a 302 redirect.
 * The frontend calls this endpoint with a Bearer token through fetch/axios,
 * which transparently follows redirects and would re-send the `Authorization`
 * header to the object store — S3/Spaces reject a request that carries both a
 * SigV4 query signature and an `Authorization` header. Returning the URL lets
 * the caller navigate to it as an unauthenticated GET, and it lets the client
 * show the expiry.
 *
 * `urlExpiresAt` is the presigned URL's own deadline (~15 min); it is shorter
 * than the export's retention `expiresAt`, and a new URL can be requested
 * until retention elapses.
 */ export const DownloadOmniscientExportResponseSchema = z.object({
    downloadUrl: z.string().min(1),
    urlExpiresAt: z.string()
});
/**
 * What `generateDocument` reports back into the chat turn: enough for the model
 * to describe the export in words and for the download card to render it, and
 * nothing else. The file bytes never travel through the model.
 *
 * `rowCount`, `omitted` and `truncated` describe the workbook, not the query the
 * model asked for — a scoped-away or capped row is simply absent, and the model
 * has to be able to say so rather than promise a completeness the file lacks.
 */ export const GenerateDocumentOutputSchema = z.object({
    downloadUrl: z.string().regex(OMNISCIENT_EXPORT_DOWNLOAD_PATH_REGEX),
    filename: z.string().min(1),
    rowCount: z.number().int().min(0),
    /** Retention deadline (ISO) — see `OmniscientExportMetadataSchema`. */ expiresAt: z.string(),
    /** True when the export hit its row cap and lists only a prefix. */ truncated: z.boolean(),
    /**
   * Rows the caller may not read, or that no longer exist, left out of the
   * workbook. Without this the model is structurally unable to learn that a
   * scoped export is incomplete and reports it as a full answer — the same
   * honesty `ChartCoverage` gives a chart.
   *
   * Defaulted rather than required because PERSISTED tool parts are read back
   * through this schema (see `ToolPartBlock`'s safeParse), so any field added
   * later must absorb rows written before it existed — without a default those
   * reload as an error card instead of the export they describe. The inferred
   * OUTPUT type still has it required, so every producer stays compiler-forced.
   */ omitted: z.number().int().min(0).default(0)
});
export const DownloadOmniscientExportErrorSchema = BaseErrorSchema;

//# sourceMappingURL=download-omniscient-export.schema.js.map